import { useEffect, type ReactNode } from "react";
import { BookOpen, Moon, Sun } from "lucide-react";

export type Theme = "light" | "dark" | "sepia";
export type Tone = "success" | "danger" | "warning" | "info" | "neutral" | "orange" | "teal" | "purple" | "magenta" | "yellow";

const LABELS: Record<string, string> = {
  DRAFT: "Draft",
  IN_REVIEW: "In review",
  APPROVED: "Approved",
  DEPRECATED: "Deprecated",
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  MANUAL: "Manual",
  TO_BE_AUTOMATED: "To automate",
  AUTOMATED: "Automated",
  PASS: "Pass",
  FAIL: "Fail",
  BLOCKED: "Blocked",
  SKIPPED: "Skipped",
  UNEXECUTED: "Not executed",
  NOT_EXECUTED: "Not executed",
  WIP: "In progress",
  FLAKY: "Flaky",
  COMPLETED: "Completed",
  ACTIVE: "Active",
  OPEN: "Open",
  UNCOVERED: "Uncovered",
  FAILED: "Failed",
  PASSED: "Passed",
  priority: "Priority",
  tag: "Tag",
  automationStatus: "Automation",
  folderId: "Folder",
};

export function labelize(value: string): string {
  if (!value) return "Any";
  return LABELS[value] ?? value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function toneFor(value: string): Tone {
  switch (value) {
    case "PASS":
    case "APPROVED":
    case "AUTOMATED":
    case "COMPLETED":
    case "PASSED":
      return "success";
    case "FAIL":
    case "FAILED":
    case "CRITICAL":
    case "DEPRECATED":
      return "danger";
    case "FLAKY":
      return "magenta";
    case "BLOCKED":
      return "orange";
    case "HIGH":
      return "warning";
    case "WIP":
      return "info";
    case "IN_REVIEW":
      return "teal";
    case "TO_BE_AUTOMATED":
      return "yellow";
    case "SKIPPED":
      return "purple";
    case "MEDIUM":
    case "ACTIVE":
      return "info";
    default:
      return "neutral";
  }
}

export function Lozenge({ value, children }: { value?: string; children?: ReactNode }) {
  const text = children ?? labelize(value ?? "");
  const tone = toneFor(value ?? "");
  return <span className={`lozenge lozenge-${tone}`}>{text}</span>;
}

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function PageHead({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Empty({ icon, title, body, action }: { icon?: ReactNode; title: string; body: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon ? <div className="empty-icon">{icon}</div> : null}
      <h2>{title}</h2>
      <p>{body}</p>
      {action}
    </div>
  );
}

export function Alert({ tone, children }: { tone: "danger" | "success" | "info"; children: ReactNode }) {
  return (
    <p role={tone === "danger" ? "alert" : "status"} className={`alert alert-${tone}`}>
      {children}
    </p>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" className="canvas stack-list">
      <p className="muted">{label}</p>
      <div className="skeleton" style={{ width: "40%" }} />
      <div className="skeleton" />
      <div className="skeleton" style={{ width: "75%" }} />
    </div>
  );
}

export function Progress({ pass, fail, blocked = 0, total }: { pass: number; fail: number; blocked?: number; total: number }) {
  const safe = Math.max(total, 0);
  const done = Math.min(safe, pass + fail + blocked);
  const pct = safe === 0 ? 0 : Math.round((done / safe) * 100);
  const width = (count: number) => (safe === 0 ? "0%" : `${(count / safe) * 100}%`);
  return (
    <div className="progress-label">
      <span className="progress" aria-hidden>
        <i className="pass" style={{ width: width(pass) }} />
        <i className="fail" style={{ width: width(fail) }} />
        <i className="blocked" style={{ width: width(blocked) }} />
      </span>
      <span>{pct}%</span>
    </div>
  );
}

export function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <article className="stat">
      <p className="label">{label}</p>
      <p className="value">{value}</p>
      <p className="detail">{detail}</p>
    </article>
  );
}

export function Field({
  name,
  label,
  required,
  placeholder,
}: {
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="stack">
      {label}
      <input className="field" name={name} required={required} placeholder={placeholder} />
    </label>
  );
}

export function ThemeSwitch({ theme, setTheme }: { theme: Theme; setTheme: (theme: Theme) => void }) {
  const options: Array<{ id: Theme; label: string; icon: typeof Sun }> = [
    { id: "light", label: "Light", icon: Sun },
    { id: "dark", label: "Dark", icon: Moon },
    { id: "sepia", label: "Sepia", icon: BookOpen },
  ];
  return (
    <div className="theme-switch" role="group" aria-label="Color theme">
      {options.map((option) => {
        const Icon = option.icon;
        return (
          <button key={option.id} type="button" aria-pressed={theme === option.id} aria-label={option.label} onClick={() => setTheme(option.id)}>
            <Icon size={16} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

export function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        className="modal"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="dialog-title">{title}</h2>
          <button className="icon-btn" type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="body">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </div>
  );
}

export function DonutChart({
  title,
  slices,
  empty,
}: {
  title: string;
  slices: Array<{ id: string; label: string; count: number; color: string }>;
  empty: string;
}) {
  const drawn = slices.filter((slice) => slice.count > 0);
  const total = drawn.reduce((sum, slice) => sum + slice.count, 0);
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const gap = drawn.length > 1 ? 3 : 0;
  let cursor = 0;
  const arcs = drawn.map((slice) => {
    const span = (slice.count / total) * circumference;
    const length = Math.max(0, span - gap);
    const arc = { ...slice, length, offset: cursor };
    cursor += span;
    return arc;
  });
  return (
    <section className="chart-card">
      <h2>{title}</h2>
      {total === 0 ? (
        <div className="chart-empty">
          <svg viewBox="0 0 160 160" aria-hidden>
            <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--line)" strokeWidth="16" />
          </svg>
          <p>{empty}</p>
        </div>
      ) : (
        <div className="chart-body">
          <svg viewBox="0 0 160 160" role="img" aria-label={`${title}, ${total} total`}>
            <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--sunken)" strokeWidth="16" />
            {arcs.map((arc) => (
              <circle
                key={arc.id}
                cx="80"
                cy="80"
                r={radius}
                fill="none"
                stroke={arc.color}
                strokeWidth="16"
                strokeDasharray={`${arc.length} ${circumference - arc.length}`}
                strokeDashoffset={-arc.offset}
                transform="rotate(-90 80 80)"
              />
            ))}
            <text x="80" y="76" textAnchor="middle" className="chart-total">
              {total}
            </text>
            <text x="80" y="94" textAnchor="middle" className="chart-caption">
              total
            </text>
          </svg>
          <ul className="chart-legend">
            {drawn.map((slice) => {
              const percent = Math.round((slice.count / total) * 1000) / 10;
              return (
                <li key={slice.id}>
                  <i style={{ background: slice.color }} />
                  <span>{slice.label}</span>
                  <strong>{slice.count}</strong>
                  <span className="muted">{percent}%</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

export function ConfigNav({ path }: { path: string }) {
  const items = [
    { href: "/settings", label: "Workflow" },
    { href: "/keys", label: "API keys" },
    { href: "/shared", label: "Shared steps" },
    { href: "/sessions", label: "Exploratory" },
  ];
  return (
    <nav aria-label="Configuration">
      {items.map((item) => (
        <a key={item.href} href={`#${item.href}`} aria-current={path === item.href ? "page" : undefined}>
          {item.label}
        </a>
      ))}
    </nav>
  );
}
